/* eslint-disable quotes */
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Modal, Button } from "react-bootstrap";
import axios from "axios";
import PulseLoader from "react-spinners/PulseLoader";
import { useNavigate } from "react-router-dom";

import { getAuthorizationToken } from "@/auth/tokenStore";

import QuarterHourTimePicker from "../../common/QuarterHourTimePicker";
import CaseHoverCell from "../../common/CaseHoverCell";
import SuccessAssignTaskModal from "../../Modals/assignTodoSuccessModal";
import incidentIcon from "../../../assets/images/incident.svg";
import birthdayIcon from "../../../assets/images/birthdayicon.svg";
import { setComponentLoadingEffect } from "../../../Redux/common/Loader/action";
import { calculateAge, formatCalendarDate, setCaseId, setClientId } from "../../../Utils/helper";
import {
  fetchAllPages,
  fetchCurrentCase,
  setCaseSummary,
} from "../../../Redux/caseData/caseDataSlice";
import { setBlurEffect } from "../../../Redux/header_blur/action";
import { fetchCaseSummary } from "../../../api/case";
import { setHeaderName } from "../../../Redux/header_name/action";
import api, { api_without_cancellation } from "../../../api/api";
import UserChatBubble from "../../Forms/FirmUserBtn";
import avatarImage from "../../../assets/images/avatar.svg";
import { ResponsivePagination } from "../../Home/modals/TaskSortedCase";

const SLOT_COUNT = 6;
const TODO_TYPE = 5;

const workersOf = (caseItem) => [
  caseItem?.case?.firm_user1,
  caseItem?.case?.firm_user2,
  caseItem?.case?.firm_user3,
  caseItem?.case?.firm_user4,
  caseItem?.case?.firm_user5,
  caseItem?.case?.firm_user6,
];

// `userId` here means the same thing it means in the individual-mode payload:
// the firm user occupying the slot. If the backend actually keys overrides on
// user type instead, swap this to the slot's typePk — only place it is read.
const overrideAssigneeId = (user /* , type */) => user?.id ?? null;

const getDefault24HoursFromNow = () => {
  const now = new Date();
  now.setHours(now.getHours() + 24);
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const StatusModal = ({ show, handleClose, selectedStatus, selectedStage }) => {
  const origin = process.env.REACT_APP_BACKEND_URL;
  const token = getAuthorizationToken();

  // Status wins over stage, exactly as the list request resolves it. Both the GET
  // and the assign payload read this one object so they cannot disagree.
  const listFilters = selectedStatus
    ? { status_id: selectedStatus.id }
    : selectedStage
      ? { stage_id: selectedStage.id }
      : null;

  // Derived, not state. Storing this in useState and assigning it inside the fetch
  // meant the label was empty until the first response landed — and stayed empty
  // if the fetch failed, shipping "Review this case and address the ".
  const label = selectedStatus
    ? `${selectedStatus?.name} Case Status`
    : selectedStage
      ? `${selectedStage?.name} Case Stage`
      : "";
  const taskLabel = `Review this case and address the ${label}`;
  const defaultTaskText = selectedStatus
    ? `Review the status ${selectedStatus?.name} on this case.`
    : selectedStage
      ? `Review the stage ${selectedStage?.name} on this case.`
      : "Review this case.";

  const [CasesData, setCasesData] = useState([]);
  const [UniqueUserTypes, setUniqueUserTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Selection model: a mode + exclusions, never a page-scoped id set.
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [excludedCaseIds, setExcludedCaseIds] = useState(new Set());
  // caseId -> Set(slotIndex). Present only for cases whose worker set the user
  // hand-edited while in all-mode. Absent = inherit global user_type_ids.
  // Deliberately NOT cleared when selectedUserTypes changes: a hand-edit is an
  // explicit statement about that case and outranks a later global toggle.
  const [caseWorkerOverrides, setCaseWorkerOverrides] = useState(new Map());

  const [selectedCases, setSelectedCases] = useState(new Set());
  const [selectedWorkers, setSelectedWorkers] = useState(new Set()); // "caseId-slotIndex"
  const [selectedUserTypes, setSelectedUserTypes] = useState(new Set()); // global, both modes
  const [checkedUsers, setCheckedUsers] = useState([]); // individual mode payload rows

  const [mainInput, setMainInput] = useState(defaultTaskText);
  const [caseInputs, setCaseInputs] = useState(new Map());
  const [dateTime, setDateTime] = useState(getDefault24HoursFromNow());
  const [dateTimeInputs, setDateTimeInputs] = useState(new Map());

  const [newCaseTasks, setNewCaseTasks] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);

  const [totalCount, setTotalCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const itemsPerPage = 50;
  const pinnedWidthsRef = useRef({});
  const lastLayoutKeyRef = useRef(null);

  // caseId -> [{ typePk, userId }] for every page loaded. Overrides can be created
  // on page 1 and submitted from page 7, so slot data has to outlive CasesData.
  const caseSlotsRef = useRef(new Map());

  useEffect(() => {
    if (show) {
      setCasesData([]);
      setUniqueUserTypes([]);
      setSelectAllMode(false);
      setExcludedCaseIds(new Set());
      setCaseWorkerOverrides(new Map());
      setSelectedCases(new Set());
      setSelectedWorkers(new Set());
      setSelectedUserTypes(new Set());
      setCheckedUsers([]);
      setMainInput(defaultTaskText);
      setCaseInputs(new Map());
      setDateTime(getDefault24HoursFromNow());
      setDateTimeInputs(new Map());
      setPageNo(1);
      pinnedWidthsRef.current = {};
      lastLayoutKeyRef.current = null;
      caseSlotsRef.current = new Map();
    }
  }, [show, defaultTaskText]);

  const fetchCasesData = async (page) => {
    if (!listFilters) return;

    setLoading(true);
    try {
      const response = await api_without_cancellation.get("/api/cases_by_status_or_stage/", {
        headers: { Authorization: token },
        params: { ...listFilters, page, page_size: itemsPerPage },
      });

      setCasesData(response.data.results.cases || []);
      setUniqueUserTypes(response.data.results.unique_user_types || []);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching cases:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    fetchCasesData(pageNo);
  }, [pageNo, show, selectedStatus, selectedStage]);

  useEffect(() => {
    CasesData.forEach((caseItem) => {
      const workers = workersOf(caseItem);
      caseSlotsRef.current.set(
        caseItem.case.id,
        Array.from({ length: SLOT_COUNT }, (_, i) => ({
          typePk: caseItem.sorted_user_types?.[i]?.pk ?? null,
          userId: workers[i]?.id ?? null,
        }))
      );
    });
  }, [CasesData]);

  const evenTypes = UniqueUserTypes.filter((_, index) => index % 2 === 0);
  const oddTypes = UniqueUserTypes.filter((_, index) => index % 2 !== 0);
  const allType = [...evenTypes, ...oddTypes];

  /* ---------- derived selection ---------- */

  const isCaseSelected = (caseId) =>
    selectAllMode ? !excludedCaseIds.has(caseId) : selectedCases.has(caseId);

  // Slots a case gets from the global type selection, ignoring any override.
  const deriveSlotsFromTypes = (caseId) => {
    const slots = caseSlotsRef.current.get(caseId) || [];
    const set = new Set();
    slots.forEach(({ typePk, userId }, slotIndex) => {
      if (typePk !== null && userId !== null && selectedUserTypes.has(typePk)) set.add(slotIndex);
    });
    return set;
  };

  const isSlotSelected = (caseItem, slotIndex) => {
    if (!selectAllMode) return selectedWorkers.has(`${caseItem.case.id}-${slotIndex}`);
    if (excludedCaseIds.has(caseItem.case.id)) return false;

    const override = caseWorkerOverrides.get(caseItem.case.id);
    if (override) return override.has(slotIndex);

    const typePk = caseItem.sorted_user_types?.[slotIndex]?.pk;
    return typePk !== null && selectedUserTypes.has(typePk);
  };

  const headerAllChecked = selectAllMode && excludedCaseIds.size === 0;
  const headerAllIndeterminate = selectAllMode && excludedCaseIds.size > 0;

  const typeLabelColor = (typePk) =>
    selectAllMode && !selectedUserTypes.has(typePk) ? "red" : "#000";

  const canSubmit = selectAllMode
    ? Boolean(listFilters) && (selectedUserTypes.size > 0 || caseWorkerOverrides.size > 0)
    : checkedUsers.length > 0;

  const loadedSelectedCases = CasesData.filter((item) => isCaseSelected(item.case.id));

  /* ---------- handlers ---------- */

  const handleAllCaseCheckbox = () => {
    const next = !headerAllChecked;
    setSelectAllMode(next);
    setExcludedCaseIds(new Set());
    setCaseWorkerOverrides(new Map());
    setSelectedCases(new Set());
    setSelectedWorkers(new Set());
    setCheckedUsers([]);
    if (!next) setSelectedUserTypes(new Set());
  };

  // Header type = "this type, on every case in the filter". Cannot coexist with a
  // hand-picked slot set, so it enters all-mode and drops individual selections.
  const handleAllUserTypesCheckbox = (userTypeId) => {
    setSelectAllMode(true);
    setSelectedCases(new Set());
    setSelectedWorkers(new Set());
    setCheckedUsers([]);
    setSelectedUserTypes((prev) => {
      const next = new Set(prev);
      if (next.has(userTypeId)) next.delete(userTypeId);
      else next.add(userTypeId);
      return next;
    });
  };

  const handleIndividualCaseCheckbox = (event, caseId) => {
    if (selectAllMode) {
      const excluding = !excludedCaseIds.has(caseId);

      setExcludedCaseIds((prev) => {
        const next = new Set(prev);
        if (excluding) next.add(caseId);
        else next.delete(caseId);
        return next;
      });

      // An excluded case has no assignees, so its override is meaningless.
      if (excluding && caseWorkerOverrides.has(caseId)) {
        setCaseWorkerOverrides((prev) => {
          const next = new Map(prev);
          next.delete(caseId);
          return next;
        });
      }
      return;
    }

    const isChecked = event.target.checked;
    const newSelectedCases = new Set(selectedCases);

    if (isChecked) {
      newSelectedCases.add(caseId);
    } else {
      newSelectedCases.delete(caseId);

      const newSelectedWorkers = new Set(selectedWorkers);
      for (let i = 0; i < SLOT_COUNT; i++) newSelectedWorkers.delete(`${caseId}-${i}`);

      setSelectedWorkers(newSelectedWorkers);
      setCheckedUsers(checkedUsers.filter((user) => user.caseId !== caseId));
    }

    setSelectedCases(newSelectedCases);
  };

  const handleIndividualUserTypesCheckbox = (event, caseId, user, slotIndex) => {
    // In all-mode a slot toggle materialises the case's currently-derived worker
    // set into an override, then flips one slot. The override rides out to the
    // server as `items` entries; global user_type_ids no longer apply to it.
    if (selectAllMode) {
      const base = caseWorkerOverrides.get(caseId) ?? deriveSlotsFromTypes(caseId);
      const nextSlots = new Set(base);
      if (nextSlots.has(slotIndex)) nextSlots.delete(slotIndex);
      else nextSlots.add(slotIndex);

      if (nextSlots.size === 0) {
        // No assignees left = the case is not being tasked. Say that with
        // excluded_case_ids rather than shipping an empty override.
        setCaseWorkerOverrides((prev) => {
          const next = new Map(prev);
          next.delete(caseId);
          return next;
        });
        setExcludedCaseIds((prev) => new Set(prev).add(caseId));
        return;
      }

      setCaseWorkerOverrides((prev) => new Map(prev).set(caseId, nextSlots));
      setExcludedCaseIds((prev) => {
        if (!prev.has(caseId)) return prev;
        const next = new Set(prev);
        next.delete(caseId);
        return next;
      });
      return;
    }

    const workerKey = `${caseId}-${slotIndex}`;
    const isChecked = !selectedWorkers.has(workerKey);
    const newSelectedWorkers = new Set(selectedWorkers);
    const newSelectedCases = new Set(selectedCases);
    let newCheckedUsers = [...checkedUsers];

    if (isChecked) {
      newSelectedWorkers.add(workerKey);
      newSelectedCases.add(caseId);

      if (!newCheckedUsers.some((item) => item.userId === user.id && item.caseId === caseId)) {
        newCheckedUsers.push({ caseId, userId: user.id });
      }
    } else {
      newSelectedWorkers.delete(workerKey);

      const caseItem = CasesData.find((item) => item.case.id === caseId);
      const workers = workersOf(caseItem);
      const hasOtherSelectedSlot = workers.some(
        (w, i) =>
          i !== slotIndex && w && w.id === user.id && newSelectedWorkers.has(`${caseId}-${i}`)
      );
      if (!hasOtherSelectedSlot) {
        newCheckedUsers = newCheckedUsers.filter(
          (item) => !(item.userId === user.id && item.caseId === caseId)
        );
      }
    }

    setSelectedWorkers(newSelectedWorkers);
    setSelectedCases(newSelectedCases);
    setCheckedUsers(newCheckedUsers);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    handleClose();
  };

  const handleMainInputChange = (event) => setMainInput(event.target.value);

  const handleCaseInputChange = (event, caseId) => {
    const value = event.target.value;
    setCaseInputs((prev) => new Map(prev).set(caseId, value));
  };

  const handleDateTimeInputChange = (event, caseId, inputType) => {
    setDateTimeInputs((prev) => {
      const currentDateTime = prev.get(caseId) || dateTime;

      let currentDate, currentTime;
      if (currentDateTime.includes("T")) {
        [currentDate, currentTime] = currentDateTime.split("T");
      } else {
        currentDate = currentDateTime.split(" ")[0] || "";
        currentTime = currentDateTime.split(" ")[1] || "";
      }

      if (inputType === "date") currentDate = event.target.value;
      else if (inputType === "time") currentTime = event.target.value;

      return new Map(prev).set(caseId, `${currentDate}T${currentTime}`);
    });
  };

  const handleDateTimeChange = (e, type) => {
    if (type === "date") {
      const currentTime = dateTime?.split("T")[1];
      setDateTime(`${e.target.value}T${currentTime}`);
    } else if (type === "time") {
      const currentDate = dateTime?.split("T")[0];
      setDateTime(`${currentDate}T${e.target.value}`);
    }

    // Row date and row text both REPLACE the top-level value. Changing the top
    // date invalidates every row date override, so they are cleared here.
    setDateTimeInputs(new Map());
  };

  /* ---------- payload ---------- */

  // One entry per (case, assignee) for cases the user touched individually while
  // in all-mode. Cases absent from `items` inherit top-level text / dateTime and
  // the global user_type_ids.
  const buildItems = () => {
    const dirtyIds = new Set();
    caseInputs.forEach((_value, caseId) => dirtyIds.add(caseId));
    dateTimeInputs.forEach((value, caseId) => {
      if (value) dirtyIds.add(caseId);
    });
    caseWorkerOverrides.forEach((_slots, caseId) => dirtyIds.add(caseId));

    const items = [];

    dirtyIds.forEach((caseId) => {
      if (excludedCaseIds.has(caseId)) return; // exclusion always wins

      const slots = caseSlotsRef.current.get(caseId);
      if (!slots) return;

      // A row inherits the top-level text until the user edits it. Once edited,
      // its value replaces the top-level text, including an intentionally empty value.
      const text = caseInputs.has(caseId) ? caseInputs.get(caseId).trim() : mainInput;
      const when = dateTimeInputs.get(caseId) || dateTime;

      const override = caseWorkerOverrides.get(caseId);
      const seen = new Set();

      slots.forEach(({ typePk, userId }, slotIndex) => {
        const chosen = override
          ? override.has(slotIndex)
          : typePk !== null && selectedUserTypes.has(typePk);
        if (!chosen) return;

        const assigneeId = overrideAssigneeId({ id: userId });
        if (assigneeId === null || seen.has(assigneeId)) return;
        seen.add(assigneeId);
        items.push({ caseId, userId: assigneeId, text, dateTime: when });
      });
    });

    return items;
  };

  const sendCheckedUsersData = async () => {
    let payload;

    if (selectAllMode) {
      if (!listFilters) return;
      const items = buildItems();
      payload = {
        apply_to_all: true,
        source: "status_or_stage",
        filters: listFilters,
        user_type_ids: [...selectedUserTypes],
        text: mainInput,
        dateTime,
        todo_type: TODO_TYPE,
        label: taskLabel,
        excluded_case_ids: [...excludedCaseIds],
        ...(items.length ? { items } : {}),
      };
    } else {
      payload = checkedUsers.map((user) => ({
        ...user,
        text: caseInputs.has(user.caseId) ? caseInputs.get(user.caseId).trim() : mainInput,
        dateTime: dateTimeInputs.get(user?.caseId) || dateTime,
        todo_type: TODO_TYPE,
        label: taskLabel,
      }));
    }

    const data = await axios.post(origin + "/api/navigator/create_navigator_todo/", payload, {
      headers: { Authorization: token },
    });

    setNewCaseTasks(data.data);
    setShowSuccessModal(true);
    setSelectAllMode(false);
    setExcludedCaseIds(new Set());
    setCaseWorkerOverrides(new Map());
    setCheckedUsers([]);
    setMainInput("");
    setCaseInputs(new Map());
    setDateTimeInputs(new Map());
    setSelectedCases(new Set());
    setSelectedWorkers(new Set());
    setSelectedUserTypes(new Set());
  };

  /* ---------- navigation / layout ---------- */

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleCaseSwitch = async (clientID, caseID, pageID) => {
    try {
      dispatch(setComponentLoadingEffect("medicalProviders", true));
      dispatch(setComponentLoadingEffect("detailBar", true));

      await api.get(`/api/switch_client/${clientID}/${caseID}/${pageID}/`);

      setClientId(clientID);
      setCaseId(caseID);
      dispatch(fetchCurrentCase(clientID, caseID));

      const caseSummaryData = await fetchCaseSummary(clientID, caseID);
      dispatch(setCaseSummary(caseSummaryData));

      dispatch(fetchAllPages(caseID));
      dispatch(setBlurEffect(false));
      dispatch(setHeaderName("Case"));

      document.body.classList.remove("modal-open", "has-blurred-bg");

      const menuContainer = document.getElementById("padding-top-165");
      if (menuContainer) menuContainer.style.filter = "";

      const divToRemove = document.querySelector(".header-blur-show");
      if (divToRemove) document.body.removeChild(divToRemove);

      navigate(`/bp-case/${clientID}/${caseID}`, { replace: true });
    } catch (error) {
      console.error(error);
    }
  };

  const handleRowNavigate = async (event, caseItem) => {
    event.preventDefault();
    const clientID = caseItem?.case?.for_client?.id;
    const caseID = caseItem?.case?.id;
    if (!clientID || !caseID) return;
    await handleCaseSwitch(clientID, caseID, 1);
  };

  const adjustCaseDetailsWidths = () => {
    const leftColumns = document.querySelectorAll(".person-index-name-case-worker-width");

    let maxLeftWidth = 0;

    leftColumns.forEach((column) => {
      maxLeftWidth = Math.max(maxLeftWidth, column.getBoundingClientRect().width);
    });

    leftColumns.forEach((column) => {
      column.style.width = `${maxLeftWidth}px`;
      column.style.minWidth = `${maxLeftWidth}px`;
      column.style.maxWidth = `${maxLeftWidth}px`;
    });
  };

  useEffect(() => {
    adjustCaseDetailsWidths();
    window.addEventListener("resize", adjustCaseDetailsWidths);
    return () => window.removeEventListener("resize", adjustCaseDetailsWidths);
  }, [CasesData]);

  const noOfPages = Math.ceil(totalCount / itemsPerPage);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const userPreferences = JSON.parse(localStorage.getItem("userPreferences") || "{}");
  const abbrUserName = userPreferences.AbbrUserName || 1;
  const modalRef = useRef(null);
  const tableRef = useRef(null);

  // Only cases on the current page can be opened — we do not hold ids for the rest.
  const handleOpenNewtab = () => {
    const baseURL = window.location.origin;

    const casesToOpen = loadedSelectedCases
      .map((item) => {
        const clientId = item.case.for_client?.id;
        if (!clientId) return null;
        return {
          caseId: item.case.id,
          clientId,
          url: `${baseURL}/bp-case/${clientId}/${item.case.id}`,
        };
      })
      .filter(Boolean);

    if (casesToOpen.length === 0) return;

    const windowRefs = casesToOpen.map((item) =>
      window.open("about:blank", `_blank_${item.caseId}`)
    );

    const blockedCount = windowRefs.filter((win) => !win).length;

    if (blockedCount > 0) {
      alert(
        `⚠️ Popups are blocked on the browser.\n\n` +
          `To fix this:\n` +
          `1. Click the popup icon in the address bar\n` +
          `2. Select "Always allow pop-ups and redirects from this site"\n\n` +
          `Then try again.`
      );
    }

    casesToOpen.forEach((item, index) => {
      const win = windowRefs[index];

      if (win && !win.closed) {
        win.document.write("<p>Loading...</p>");
        win.document.close();
        win.location.href = item.url;
      } else {
        window.open(item.url, "_blank");
      }
    });
  };

  // Due popup: which button is open + where to render it
  const [duePopup, setDuePopup] = useState(false);
  const [duePopupKey, setDuePopupKey] = useState(null); // "main" | caseId

  const openDuePopup = (key) => {
    setDuePopupKey(key);
    setDuePopup(true);
  };

  const closeDuePopup = () => setDuePopup(false);

  const duePopupValue =
    duePopupKey === "main" ? dateTime : dateTimeInputs.get(duePopupKey) || dateTime;

  const formatDueParts = (dt) => {
    if (!dt) return { day: "", date: "", time: "" };
    const normalized = dt.includes("T") ? dt : dt.replace(" ", "T");
    const [datePart, timePart] = normalized.split("T");
    if (!datePart) return { day: "", date: "", time: "" };
    const [y, m, d] = datePart.split("-").map(Number);
    const dateObj = new Date(y, m - 1, d);
    const day = isNaN(dateObj) ? "" : dateObj.toLocaleDateString("en-US", { weekday: "long" });
    const date = `${m}/${String(d).padStart(2, "0")}/${y}`;
    let time = "";
    if (timePart) {
      // eslint-disable-next-line prefer-const
      let [hh, mm] = timePart.split(":").map(Number);
      if (!isNaN(hh)) {
        const ampm = hh >= 12 ? "pm" : "am";
        hh = hh % 12 || 12;
        time = `${hh}:${String(mm || 0).padStart(2, "0")} ${ampm}`;
      }
    }
    return { day, date, time };
  };

  const adjustReportColumnWidths = () => {
    // The instructions column flexes to fill leftover width. Release it before
    // measuring so the fixed columns report their true content width.
    const layoutKey =
      abbrUserName === 1
        ? width > 1625
          ? "1:2"
          : "1:1"
        : abbrUserName === 4
          ? width < 1485
            ? "4:1"
            : "4:2"
          : `${abbrUserName}:1`;

    if (lastLayoutKeyRef.current !== layoutKey) {
      pinnedWidthsRef.current = {};
      lastLayoutKeyRef.current = layoutKey;
    }

    const setTask = (style) => {
      document.querySelectorAll(".task-popup-col").forEach((c) => {
        c.style.width = style.width;
        c.style.minWidth = style.minWidth;
        c.style.maxWidth = style.maxWidth;
      });
    };

    const pinColumn = (selector, minWidth = 0) => {
      const cols = document.querySelectorAll(selector);
      if (!cols.length) return;

      cols.forEach((col) => {
        col.style.width = "";
        col.style.minWidth = minWidth ? `${minWidth}px` : "";
        col.style.maxWidth = "";
      });

      let natural = 0;
      cols.forEach((col) => {
        natural = Math.max(natural, col.getBoundingClientRect().width);
      });

      // Ratchet: never below the widest this column has ever needed across
      // page changes, so headers don't move once they've grown.
      const pinned = Math.max(minWidth, pinnedWidthsRef.current[selector] || 0, natural);
      pinnedWidthsRef.current[selector] = pinned;

      cols.forEach((col) => {
        col.style.width = `${pinned}px`;
        col.style.minWidth = `${pinned}px`;
        col.style.maxWidth = `${pinned}px`;
      });
    };

    setTask({ width: "1px", minWidth: "0px", maxWidth: "none" });
    pinColumn(".case-popup-col", 220);
    pinColumn(".due-popup-col");
    pinColumn(".worker-popup-col");
    setTask({ width: "auto", minWidth: "", maxWidth: "" });
  };

  useEffect(() => {
    if (loading) return;
    const raf = requestAnimationFrame(adjustReportColumnWidths);
    window.addEventListener("resize", adjustReportColumnWidths);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", adjustReportColumnWidths);
    };
  }, [CasesData, pageNo, loading, width, abbrUserName]);

  const renderDueButton = (key, dtValue) => {
    const { day, date, time } = formatDueParts(dtValue);
    return (
      <button
        type="button"
        className="due-button-trigger btn-hover-document-row d-flex flex-column align-items-start justify-content-start cursor-pointer"
        style={{
          height: "84px",
          boxSizing: "border-box",
          minWidth: "110px",
          border: "1px solid var(--primary)",
          color: "var(--primary)",
          fontSize: "14px",
          lineHeight: "21px",
          padding: "0 5px",
          whiteSpace: "nowrap",
          background: "var(--primary-10)",
        }}
        onClick={() => openDuePopup(key)}
      >
        <span className="height-21 flex-shrink-0" style={{ fontWeight: "600" }}>
          {day}
        </span>
        <span className="height-21 flex-shrink-0" style={{ fontWeight: "600" }}>
          {date}
        </span>
        <span className="height-21 flex-shrink-0 mt-auto" style={{ fontWeight: "600" }}>
          {time}
        </span>
      </button>
    );
  };

  const gridColumns =
    abbrUserName === 1
      ? width > 1625
        ? "1fr 1fr"
        : "1fr"
      : abbrUserName === 4
        ? width < 1485
          ? "1fr"
          : "1fr 1fr"
        : "1fr";

  const gridColumnGap =
    abbrUserName === 1
      ? width > 1625
        ? "15px"
        : ""
      : abbrUserName === 4
        ? width < 1485
          ? ""
          : "15px"
        : "";

  const gridRowGap =
    abbrUserName === 1
      ? width > 1625
        ? "5px"
        : ""
      : abbrUserName === 4
        ? width < 1485
          ? ""
          : "5px"
        : "";

  const textareaHeight =
    abbrUserName === 1
      ? width > 1625
        ? "84px"
        : "126px"
      : abbrUserName === 4
        ? width < 1485
          ? "126px"
          : "84px"
        : "126px";

  const stickyHeaderTop =
    abbrUserName === 1
      ? width > 1625
        ? "119px"
        : "161px"
      : abbrUserName === 4
        ? width < 1485
          ? "161px"
          : "119px"
        : "161px";

  return (
    <>
      {!showSuccessModal && (
        <Modal
          show={show}
          onHide={handleClose}
          centered
          dialogClassName="modal-dialog modal-dialog-centered max-width-tasks-modal"
          style={{ opacity: duePopup ? 0.7 : showSuccessModal ? 0.7 : 1 }}
        >
          <Modal.Header className="text-center p-0 height-25 bg-primary popup-heading-color justify-content-center modal-header">
            <Modal.Title style={{ fontSize: "16px", textTransform: "uppercase" }}>
              {selectedStatus
                ? `Assign Tasks to Cases with the Status ${selectedStatus?.name}`
                : selectedStage
                  ? `Assign Tasks to Cases with the Stage ${selectedStage?.name}`
                  : "Assign Tasks to Sorted Cases"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body
            ref={modalRef}
            style={{ maxHeight: "max-content", overflowY: "auto" }}
            className="p-0 overflow-x-scroll invisible-scrollbar"
          >
            <table
              ref={tableRef}
              className="table table-borderless table-striped table-earning has-height-25 additional-instructions navigator-standard-table-header case-hover-table"
              id="tasks-table-generator-table-id"
            >
              <thead>
                <tr>
                  <th
                    className="td-autosize"
                    style={{ minWidth: "25px", maxWidth: "25px", width: "25px" }}
                  />
                  <th
                    className="td-autosize"
                    style={{ minWidth: "25px", maxWidth: "25px", width: "25px" }}
                  />
                  <th className="case-popup-col td-autosize" style={{ minWidth: "220px" }} />
                  <th className="text-uppercase td-autosize due-popup-col">Due</th>
                  <th className="text-uppercase td-autosize task-popup-col">
                    Instructions for Each Task
                  </th>
                  <th className="text-uppercase td-autosize worker-popup-col">Case Workers</th>
                </tr>
              </thead>

              <tbody>
                <tr className="has-white-td" style={{ background: "var(--primary-2)" }}>
                  <td
                    className="td-autosize"
                    style={{ minWidth: "25px", maxWidth: "25px", width: "25px" }}
                  >
                    <div className="h-100 d-flex align-items-start">
                      <input
                        ref={(el) => {
                          if (el) el.indeterminate = headerAllIndeterminate;
                        }}
                        onChange={handleAllCaseCheckbox}
                        checked={headerAllChecked}
                        className="all_checkbox_check align-middle cursor-pointer"
                        type="checkbox"
                        style={{ marginTop: "3px", width: "15px", height: "15px" }}
                      />
                    </div>
                  </td>
                  <td />
                  <td className="text-start white_space-wrap assign-task-container">
                    <div className="assign-task-div-2 h-100 d-flex align-items-start white-space">
                      Assign Task to All
                      {totalCount > 0 && <span className="d-block p-r-5 p-l-5">{totalCount}</span>}
                      Cases
                    </div>
                  </td>

                  <td className="td-autosize text-center align-middle">
                    {renderDueButton("main", dateTime)}
                  </td>

                  <td className="text-start">
                    <div className="d-flex align-items-center" style={{ gap: "5px" }}>
                      <div
                        className="d-flex"
                        style={{
                          width: "100%",
                          gap: "10px",
                          minHeight: "84px",
                          maxHeight: "136px",
                        }}
                      >
                        <textarea
                          className="form-control"
                          style={{
                            flex: 1,
                            minHeight: "84px",
                            maxHeight: "136px",
                            height: textareaHeight,
                            resize: "vertical",
                          }}
                          placeholder=""
                          value={mainInput}
                          onChange={handleMainInputChange}
                        />
                      </div>
                    </div>
                  </td>

                  <td className="td-autosize">
                    <div
                      className="m-0 main-checkboxes"
                      style={{
                        display: "grid",
                        gridTemplateColumns: gridColumns,
                        columnGap: gridColumnGap,
                        rowGap: gridRowGap,
                      }}
                    >
                      {allType?.map((type, index) => {
                        const parsedType = JSON.parse(type)[0];
                        const isChecked = selectedUserTypes.has(parsedType.pk);

                        return (
                          <div
                            key={parsedType.pk}
                            className="checkbox-wrapper d-flex align-items-center justify-content-between"
                            style={{ gap: "5px", cursor: "pointer" }}
                            onClick={() => handleAllUserTypesCheckbox(parsedType.pk)}
                          >
                            <span className="d-flex align-items-center">
                              <input
                                data-usertype-id={parsedType.pk}
                                data-user_id=""
                                className="all-workers-types cursor-pointer"
                                type="checkbox"
                                checked={isChecked}
                                readOnly
                                id={`edit_header_worker_even_${index}`}
                              />
                              <label
                                className="m-l-5 text-start mb-0"
                                htmlFor={`edit_header_worker_even_${index}`}
                                style={{
                                  pointerEvents: "none",
                                  color: typeLabelColor(parsedType.pk),
                                }}
                              >
                                {parsedType?.fields?.name}
                              </label>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              </tbody>

              <tbody className="all_case_tbody_case_age-task-gen">
                <tr className="header" style={{ top: stickyHeaderTop }}>
                  <th
                    className="td-autosize"
                    style={{ minWidth: "25px", maxWidth: "25px", width: "25px" }}
                  />
                  <th
                    className="td-autosize columnCounter"
                    style={{ minWidth: "25px", maxWidth: "25px", width: "25px" }}
                  />
                  <th
                    className="td-autosize case-popup-col text-center"
                    style={{ minWidth: "220px" }}
                  >
                    Case
                  </th>
                  <th className="due-popup-col td-autosize" />
                  <th className="task-popup-col td-autosize" />
                  <th className="text-end td-autosize worker-popup-col">
                    {totalCount > itemsPerPage && (
                      <ResponsivePagination
                        position="top"
                        pageNo={pageNo}
                        setPageNo={setPageNo}
                        totalPages={noOfPages}
                      />
                    )}
                  </th>
                </tr>

                {!loading &&
                  CasesData.map((caseItem, index) => {
                    const workers = workersOf(caseItem);
                    const firmUsers = Array.from({ length: SLOT_COUNT }, (_, slotIndex) => ({
                      user: workers[slotIndex],
                      type: caseItem.sorted_user_types?.[slotIndex],
                      slotIndex,
                    }));

                    const caseSelected = isCaseSelected(caseItem.case.id);
                    const age = calculateAge(caseItem.case?.for_client?.birthday);

                    return (
                      <tr
                        key={caseItem.case.id}
                        data-case_id={caseItem.case.id}
                        className={`case-hover-row checklist-wo-header-height-63px ${caseItem.span_class_name} ${caseItem.state_name} ${caseItem.county_name} ${caseItem.injury_class_names}`}
                      >
                        <td style={{ minWidth: "25px", maxWidth: "25px", width: "25px" }}>
                          <div className="h-100 d-flex align-items-start">
                            <input
                              type="checkbox"
                              data-case-id={caseItem.case.id}
                              checked={caseSelected}
                              style={{ marginTop: "3px", width: "15px", height: "15px" }}
                              className={`case-row-checkboxes cursor-pointer case-row-checkbox-${caseItem.case.id}`}
                              onChange={(event) =>
                                handleIndividualCaseCheckbox(event, caseItem.case.id)
                              }
                            />
                          </div>
                        </td>

                        <td
                          style={{
                            minWidth: "25px",
                            maxWidth: "25px",
                            width: "25px",
                            color: "var(--primary-25)",
                            fontWeight: "600",
                          }}
                        >
                          <div className="h-100 text-center">
                            {(pageNo - 1) * itemsPerPage + index + 1}
                          </div>
                        </td>

                        <CaseHoverCell
                          caseData={caseItem.case}
                          onCaseClick={(event) => handleRowNavigate(event, caseItem)}
                        >
                          <div className="d-flex person-index-name-case-name flex-column align-items-start w-100">
                            <div
                              className="d-flex align-items-center height-21"
                              style={{ gap: "5px", color: "black", fontWeight: "bold" }}
                            >
                              <span className="ic-avatar ic-15 has-avatar-icon has-cover-img">
                                <img
                                  className="output-18 theme-ring"
                                  src={
                                    caseItem.case.for_client.profile_pic_29p
                                      ? caseItem.case.for_client.profile_pic_29p
                                      : avatarImage
                                  }
                                  style={{ background: "var(--primary-30)" }}
                                />
                              </span>
                              <p className="">
                                {caseItem.case.for_client?.last_name},{" "}
                                {caseItem.case.for_client?.first_name}
                              </p>
                            </div>

                            <div
                              style={{ gap: "5px", color: "black", fontWeight: "600" }}
                              className="d-flex align-items-center justify-content-between height-21 w-100"
                            >
                              <div className="d-flex align-items-center" style={{ gap: "5px" }}>
                                <span className="ic-avatar ic-15 d-flex">
                                  <img src={birthdayIcon} />
                                </span>
                                <span>
                                  {formatCalendarDate(caseItem.case.for_client?.birthday)}
                                </span>
                              </div>
                              <div className="d-flex align-items-center" style={{ gap: "5px" }}>
                                <span className="text-primary-25 me-0">age</span>
                                <span
                                  className="ms-0 flex-shrink-0"
                                  style={{ width: Number.isFinite(age) ? undefined : "18px" }}
                                >
                                  {Number.isFinite(age) ? age : ""}
                                </span>
                              </div>
                            </div>

                            <div
                              style={{ gap: "5px", color: "black", fontWeight: "bold" }}
                              className="d-flex align-items-center height-21"
                            >
                              <span className="ic-avatar d-flex align-items-center justify-content-center ic-15">
                                <img src={caseItem.case.case_type?.casetype_icon} />
                              </span>
                              {caseItem.case.case_type?.name}
                            </div>

                            <div
                              style={{ gap: "5px", color: "black", fontWeight: "bold" }}
                              className="d-flex align-items-center height-21"
                            >
                              <span className="ic-avatar ic-15 d-flex">
                                <img src={incidentIcon} className="" />
                              </span>
                              <span>{formatCalendarDate(caseItem.case?.incident_date)}</span>
                            </div>
                          </div>
                        </CaseHoverCell>

                        <td className="td-autosize text-center align-middle">
                          {renderDueButton(
                            caseItem.case.id,
                            dateTimeInputs.get(caseItem.case.id) || dateTime
                          )}
                        </td>

                        <td className="text-start">
                          <div
                            className="d-flex align-items-center"
                            style={{ gap: "5px", minHeight: "84px", maxHeight: "136px" }}
                          >
                            <div
                              className="d-flex"
                              style={{
                                width: "100%",
                                gap: "10px",
                                minHeight: "84px",
                                maxHeight: "136px",
                              }}
                            >
                              <textarea
                                className="form-control"
                                style={{
                                  flex: 1,
                                  minHeight: "84px",
                                  maxHeight: "136px",
                                  height: textareaHeight,
                                  resize: "vertical",
                                }}
                                placeholder=""
                                value={
                                  caseInputs.has(caseItem.case.id)
                                    ? caseInputs.get(caseItem.case.id)
                                    : mainInput
                                }
                                onChange={(event) => handleCaseInputChange(event, caseItem.case.id)}
                              />
                            </div>
                          </div>
                        </td>

                        <td className="td-autosize">
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: gridColumns,
                              columnGap: gridColumnGap,
                              rowGap: gridRowGap,
                            }}
                            className="case-workers-grid m-0 main-checkboxes"
                          >
                            {firmUsers.filter(({ type }) => type?.fields?.name).length === 0 ? (
                              <span style={{ gridColumn: "1 / -1" }}>No Type Available</span>
                            ) : (
                              firmUsers.map(({ user, type, slotIndex }) => {
                                if (!type?.fields?.name) return null;
                                const workerKey = `${caseItem.case.id}-${slotIndex}`;
                                const isChecked = isSlotSelected(caseItem, slotIndex);

                                return (
                                  <div
                                    className="checkbox-wrapper person-index-name-case-worker-width d-flex align-items-center justify-content-between"
                                    style={{ gap: "5px", cursor: user ? "pointer" : "default" }}
                                    key={workerKey}
                                    onClick={(event) =>
                                      user &&
                                      handleIndividualUserTypesCheckbox(
                                        event,
                                        caseItem.case.id,
                                        user,
                                        slotIndex
                                      )
                                    }
                                  >
                                    <span className="d-flex align-items-center">
                                      <input
                                        checked={isChecked}
                                        readOnly
                                        disabled={!user}
                                        className={user ? "cursor-pointer" : ""}
                                        type="checkbox"
                                        id={`case_worker_${caseItem.case.id}_${slotIndex}`}
                                      />
                                      <label
                                        className="m-l-5 text-start mb-0"
                                        htmlFor={`case_worker_${caseItem.case.id}_${slotIndex}`}
                                        style={{
                                          pointerEvents: "none",
                                          color: "#000",
                                          display: "inline-grid",
                                        }}
                                      >
                                        <span
                                          aria-hidden="true"
                                          style={{
                                            gridArea: "1 / 1",
                                            fontWeight: 600,
                                            visibility: "hidden",
                                          }}
                                        >
                                          {type?.fields?.name}
                                        </span>

                                        <span
                                          style={{
                                            gridArea: "1 / 1",
                                            fontWeight: caseSelected && isChecked ? 600 : 400,
                                          }}
                                        >
                                          {type?.fields?.name}
                                        </span>
                                      </label>
                                    </span>
                                    {user && (
                                      <span className="d-flex align-items-center">
                                        <UserChatBubble
                                          userId={user?.user?.id}
                                          first_name={user?.user?.first_name}
                                          last_name={user?.user?.last_name}
                                          profilePic={user?.user?.profile_pic_29p ?? ""}
                                          is_active={user?.user?.is_active}
                                          width={19}
                                          isActive={
                                            userOnlineStatus?.[user?.user?.id]?.isOnline ?? false
                                          }
                                          hideIcon={true}
                                        />
                                      </span>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                {loading && (
                  <tr>
                    <td colSpan="6" className="text-center">
                      <PulseLoader
                        loading={true}
                        size={14}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                        color="#19395F"
                        style={{ paddingTop: "5px" }}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Modal.Body>

          <Modal.Footer
            style={{ background: "var(--primary-25)" }}
            className=" p-0 mt-0 padding-outside-btn-new-provider"
          >
            <Button
              variant="secondary"
              className="button-padding-footer-new-provider d-flex align-items-center justify-content-center  btn btn-secondary btn btn-secondary"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ gap: "5px" }}
            >
              <Button
                variant="success"
                className="button-padding-footer-new-provider d-flex align-items-center justify-content-center  btn"
                onClick={handleOpenNewtab}
                disabled={loadedSelectedCases.length === 0}
              >
                Open Checked Cases in New Tabs
              </Button>
              <Button
                id="createTodoSaveBtn"
                variant="success"
                disabled={!canSubmit}
                onClick={sendCheckedUsersData}
                className="button-padding-footer-new-provider d-flex align-items-center justify-content-center  btn btn "
              >
                Assign Tasks to Checked Cases
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}

      {showSuccessModal && (
        <SuccessAssignTaskModal
          show={showSuccessModal}
          setRes={setNewCaseTasks}
          handleClose={handleCloseSuccessModal}
          res={newCaseTasks}
        />
      )}

      {duePopup && (
        <Modal show={duePopup} onHide={closeDuePopup} centered style={{ zIndex: 2000 }}>
          <Modal.Header
            style={{ borderBottom: "0px" }}
            className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center"
          >
            <Modal.Title
              className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
              id="modal_title"
              style={{ fontSize: "14px", fontWeight: "600" }}
            >
              Set Date &amp; Time
            </Modal.Title>
          </Modal.Header>

          <Modal.Body style={{ padding: "5px" }}>
            <div className="mb-3 m-b-5">
              <label className="m-0" style={{ color: "var(--primary-25)", fontWeight: "600" }}>
                Date
              </label>
              <input
                type="date"
                className="form-control height-25"
                value={duePopupValue?.split("T")[0] || ""}
                onChange={(event) =>
                  duePopupKey === "main"
                    ? handleDateTimeChange(event, "date")
                    : handleDateTimeInputChange(event, duePopupKey, "date")
                }
              />
            </div>

            <div className="mb-3 mb-0">
              <label className="m-0" style={{ color: "var(--primary-25)", fontWeight: "600" }}>
                Time
              </label>
              <QuarterHourTimePicker
                value={duePopupValue?.split("T")[1] || ""}
                onChange={(event) =>
                  duePopupKey === "main"
                    ? handleDateTimeChange(event, "time")
                    : handleDateTimeInputChange(event, duePopupKey, "time")
                }
              />
            </div>
          </Modal.Body>

          <Modal.Footer
            className="p-0 mt-0 padding-outside-btn-new-provider"
            style={{ borderTop: "none" }}
          >
            <button
              className="button-padding-footer-new-provider d-flex align-items-center justify-content-center btn btn-secondary "
              onClick={closeDuePopup}
            >
              Cancel
            </button>
            <button
              className="button-padding-footer-new-provider d-flex align-items-center justify-content-center btn btn-primary "
              onClick={closeDuePopup}
            >
              Done
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

StatusModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  selectedStatus: PropTypes.object,
  selectedStage: PropTypes.object,
};

export default StatusModal;
